import { useState, useEffect } from "react";
import { X, Settings, AlertCircle, RefreshCw } from "lucide-react";
import { useGetSites } from "../../hooks/site/useGetSites";
import { useGetPostesEmission } from "../../hooks/emission/useGetPostesEmission";
import { useUpdateSiteConfig } from "../../hooks/site/useUpdateSiteConfig";
import { useGetPosteIndicateurs } from "../../hooks/emission/useGetPosteIndicateurs";
import { useGetSiteConfig } from "../../hooks/site/useGetSiteConfig";
import type { PosteEmission } from "../../types/postesEmission";
import type { PosteIndicateur } from "../../types/postesIndicateurs";
import type { ChampMetier } from "../../types/champMetier";
import "./ConfigDialog.css";
import { SiteComboBox } from "../SiteComboBox";
import { normalizeSiteConfigJson } from "../../Utils/siteConfig";

// NEW IMPORT

interface ConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SiteConfig {
  siteId: number;
  postesConfig: {
    [posteId: number]: {
      indicateurs: {
        id: number;
        obligatoire: boolean;
      }[];
    };
  };
}

// ChampMetier and PosteIndicateur have similar structure
type IndicateurType = ChampMetier | PosteIndicateur;

interface PosteIndicateursData {
  [posteId: number]: IndicateurType[];
}

export const ConfigDialog: React.FC<ConfigDialogProps> = ({ isOpen, onClose }) => {
  const [selectedSite, setSelectedSite] = useState<number | null>(null);
  const [activePosteId, setActivePosteId] = useState<number | null>(null);
  const [siteConfig, setSiteConfig] = useState<SiteConfig>({
    siteId: 0,
    postesConfig: {},
  });
  const [posteIndicateursData, setPosteIndicateursData] = useState<PosteIndicateursData>({});

  const { data: sites, isLoading: sitesLoading } = useGetSites();
  const { data: postes, isLoading: postesLoading } = useGetPostesEmission();
  const updateSiteConfigMutation = useUpdateSiteConfig();

  // Load existing site configuration using the dedicated hook
  const { data: siteConfigData, isLoading: configLoading } = useGetSiteConfig(selectedSite);
  const existingConfig = siteConfigData?.config_json;

  // Reset all form state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSite(null);
      setActivePosteId(null);
      setSiteConfig({
        siteId: 0,
        postesConfig: {},
      });
      setPosteIndicateursData({});
    }
  }, [isOpen]);

  // Update the useEffect to load existing config
  useEffect(() => {
    if (!selectedSite || !postes) return;

    setSiteConfig((prev) => {
      if (prev.siteId === selectedSite && Object.keys(prev.postesConfig).length > 0) {
        return prev; // Don't reset if already loaded
      }

      const initialConfig: SiteConfig = {
        siteId: selectedSite,
        postesConfig: {},
      };

      // Initialize with empty arrays
      postes.forEach((poste) => {
        initialConfig.postesConfig[poste.id] = {
          indicateurs: [],
        };
      });

      // Use existing config_json which already contains obligatoire information
      // Supports both backend object cache format and legacy array format.
      const normalized = normalizeSiteConfigJson(existingConfig);

      normalized.forEach((config: any) => {
        const posteId = config.poste;
        if (!initialConfig.postesConfig[posteId]) {
          initialConfig.postesConfig[posteId] = { indicateurs: [] };
        }

        const indicateurs = (config.indicateurs || []).map((item: any) => {
          if (typeof item === "object" && item?.id !== undefined) {
            return {
              id: item.id,
              obligatoire: Boolean(item.obligatoire),
            };
          }

          return {
            id: typeof item === "number" ? item : Number(item?.id),
            obligatoire: false,
          };
        });

        initialConfig.postesConfig[posteId].indicateurs = indicateurs;
      });

      return initialConfig;
    });

    setPosteIndicateursData({});
  }, [selectedSite, postes, existingConfig]);

  // Auto-select first poste when postes load and site is selected
  useEffect(() => {
    if (postes && postes.length > 0 && selectedSite && !activePosteId) {
      setActivePosteId(postes[0].id);
    }
  }, [postes, selectedSite, activePosteId]);

  const handleSiteSelect = (siteId: number | null) => {
    setSelectedSite(siteId);
    setActivePosteId(null);
    // Clear poste indicateurs data when changing sites
    setPosteIndicateursData({});
  };

  const handleIndicateurToggle = (posteId: number, indicateurId: number) => {
    setSiteConfig((prev) => {
      // ✅ DEEP COPY - This is crucial!
      const newConfig: SiteConfig = {
        siteId: prev.siteId,
        postesConfig: { ...prev.postesConfig },
      };

      // Ensure the poste config exists
      if (!newConfig.postesConfig[posteId]) {
        newConfig.postesConfig[posteId] = { indicateurs: [] };
      }

      // ✅ Create a new copy of the poste config
      const currentIndicateurs = newConfig.postesConfig[posteId].indicateurs || [];
      const newIndicateurs = [...currentIndicateurs];

      const index = newIndicateurs.findIndex((ind) => ind.id === indicateurId);

      if (index > -1) {
        newIndicateurs.splice(index, 1);
      } else {
        newIndicateurs.push({ id: indicateurId, obligatoire: false });
      }

      // ✅ Create entirely new object for this poste
      newConfig.postesConfig[posteId] = {
        indicateurs: newIndicateurs,
      };

      return newConfig;
    });
  };

  const handleObligatoireToggle = (posteId: number, indicateurId: number) => {
    setSiteConfig((prev) => {
      const newConfig: SiteConfig = {
        siteId: prev.siteId,
        postesConfig: { ...prev.postesConfig },
      };

      if (!newConfig.postesConfig[posteId]) return prev;

      const currentIndicateurs = [...newConfig.postesConfig[posteId].indicateurs];
      const index = currentIndicateurs.findIndex((ind) => ind.id === indicateurId);

      if (index > -1) {
        currentIndicateurs[index] = {
          ...currentIndicateurs[index],
          obligatoire: !currentIndicateurs[index].obligatoire,
        };

        newConfig.postesConfig[posteId] = {
          indicateurs: currentIndicateurs,
        };
      }

      return newConfig;
    });
  };

  const handleSave = async () => {
    if (!selectedSite) return;

    try {
      // Create the configs array in the format expected by the backend
      const configPayload = {
        configs: Object.entries(siteConfig.postesConfig).flatMap(([posteId, config]) =>
          config.indicateurs.map((indicateur) => ({
            champ_metier_id: indicateur.id,
            obligatoire: indicateur.obligatoire,
          }))
        ),
      };

      await updateSiteConfigMutation.mutateAsync({
        siteId: selectedSite,
        config: configPayload,
      });

      alert("Configuration sauvegardée avec succès!");
      onClose();
    } catch (error) {
      console.error("Error saving config:", error);
      alert("Erreur lors de la sauvegarde de la configuration");
    }
  };

  const getSelectedSiteName = () => {
    if (!selectedSite || !sites) return null;
    const site = sites.find((s) => s.id === selectedSite);
    return site ? site.name : null;
  };

  if (!isOpen) return null;

  // Update isLoading check
  const isLoading = sitesLoading || postesLoading || configLoading;

  return (
    <div className="config-dialog-overlay">
      <div className={`config-dialog ${selectedSite ? "expanded" : "compact"}`}>
        <div className="config-dialog-header">
          <div className="header-content">
            <Settings className="header-icon" size={24} />
            <div>
              <h2>Configuration des Sites</h2>
              <p>Configurez les postes d'émission et indicateurs pour chaque site</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="config-dialog-body">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Chargement des données...</p>
            </div>
          ) : (
            <>
              {/* Sites Selection */}
              <div className="sites-section">
                <div className="selection-wrapper">
                  <div className="site-select-wrapper">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                      }}
                    >
                      Site
                    </label>
                    <SiteComboBox
                      value={selectedSite}
                      placeholder="Choisir un site..."
                      onChange={(selected) => {
                        // Handle both single and multi-select, but we only want single here
                        const siteId = Array.isArray(selected) ? selected[0] : selected;
                        handleSiteSelect(siteId);
                      }}
                      inputId="config-site-select"
                      isClearable={true}
                    />
                  </div>
                </div>

                {selectedSite && getSelectedSiteName() && (
                  <div className="selected-info" style={{ marginTop: "16px" }}>
                    <div className="selected-site-info">
                      <div className="site-info-card">
                        <div className="site-info-validation">
                          {sites?.find((s) => s.id === selectedSite)?.require_double_validation
                            ? "Double validation"
                            : "Validation simple"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Postes and Indicateurs Configuration */}
              {selectedSite && (
                <div className="postes-section">
                  <h3>Postes d'émission et indicateurs</h3>

                  {/* Tabs for postes */}
                  <div className="postes-tabs">
                    {postes?.map((poste: PosteEmission) => {
                      const selectedCount =
                        siteConfig.postesConfig[poste.id]?.indicateurs?.length || 0;
                      return (
                        <button
                          key={poste.id}
                          className={`poste-tab ${activePosteId === poste.id ? "active" : ""}`}
                          onClick={() => setActivePosteId(poste.id)}
                        >
                          {poste.name}
                          {selectedCount > 0 && (
                            <span className="poste-tab-badge">{selectedCount}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Content for active poste */}
                  <div className="postes-list">
                    {postes?.map((poste: PosteEmission) => (
                      <PostePanel
                        key={poste.id}
                        poste={poste}
                        isActive={activePosteId === poste.id}
                        siteConfig={siteConfig}
                        onIndicateurToggle={handleIndicateurToggle}
                        onObligatoireToggle={handleObligatoireToggle}
                        posteIndicateursData={posteIndicateursData}
                        setPosteIndicateursData={setPosteIndicateursData}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="config-dialog-footer">
          <button className="btn-cancel" onClick={onClose}>
            Annuler
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={!selectedSite || updateSiteConfigMutation.isPending}
          >
            {updateSiteConfigMutation.isPending ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------
// PostePanel (unchanged)
// ----------------------------------

interface PostePanelProps {
  poste: PosteEmission;
  isActive: boolean;
  siteConfig: SiteConfig;
  onIndicateurToggle: (posteId: number, indicateurId: number) => void;
  onObligatoireToggle: (posteId: number, indicateurId: number) => void;
  posteIndicateursData: PosteIndicateursData;
  setPosteIndicateursData: React.Dispatch<React.SetStateAction<PosteIndicateursData>>;
}

const PostePanel: React.FC<PostePanelProps> = ({
  poste,
  isActive,
  siteConfig,
  onIndicateurToggle,
  onObligatoireToggle,
  posteIndicateursData,
  setPosteIndicateursData,
}) => {
  const {
    data: indicateurs,
    isLoading: indicateursLoading,
    error: indicateursError,
    refetch,
  } = useGetPosteIndicateurs({
    posteId: poste.id,
    enabled: isActive,
  });

  useEffect(() => {
    if (indicateurs) {
      setPosteIndicateursData((prev) => ({
        ...prev,
        [poste.id]: indicateurs,
      }));
    }
  }, [indicateurs, poste.id, setPosteIndicateursData]);

  const isIndicateurSelected = (indicateurId: number) => {
    return Boolean(
      siteConfig.postesConfig[poste.id]?.indicateurs?.some((ind) => ind.id === indicateurId)
    );
  };

  const isIndicateurObligatoire = (indicateurId: number) => {
    const indicateur = siteConfig.postesConfig[poste.id]?.indicateurs?.find(
      (ind) => ind.id === indicateurId
    );
    return indicateur?.obligatoire || false;
  };

  const handleRetry = () => {
    refetch();
  };

  return (
    <div className={`poste-panel ${isActive ? "active" : ""}`}>
      <div className="poste-content">
        {indicateursLoading && (
          <div className="indicateurs-loading">
            <div className="loading-spinner-small"></div>
            <span>Chargement des indicateurs pour {poste.name}...</span>
          </div>
        )}

        {indicateursError && (
          <div className="indicateurs-error">
            <div className="error-content">
              <AlertCircle size={20} className="error-icon" />
              <div className="error-details">
                <span className="error-title">Erreur lors du chargement des indicateurs</span>
                <span className="error-message">{indicateursError.message}</span>
                <span className="error-poste">
                  Poste: {poste.name} (ID: {poste.id})
                </span>
              </div>
              <button className="retry-button" onClick={handleRetry}>
                <RefreshCw size={16} />
                Réessayer
              </button>
            </div>
          </div>
        )}

        {indicateurs && indicateurs.length > 0 && (
          <div className="indicateurs-grid">
            {indicateurs.map((indicateur: IndicateurType) => {
              // Handle both ChampMetier (unite_defaut) and PosteIndicateur (unite_default)
              const uniteDefault =
                "unite_defaut" in indicateur ? indicateur.unite_defaut : indicateur.unite_default;

              return (
                <div key={indicateur.id} className="indicateur-item">
                  <label className="indicateur-checkbox">
                    <input
                      type="checkbox"
                      checked={isIndicateurSelected(indicateur.id)}
                      onChange={() => onIndicateurToggle(poste.id, indicateur.id)}
                    />
                    <div className="checkbox-content">
                      <span className="indicateur-name">
                        {indicateur.code} - {indicateur.libelle}
                      </span>
                      <span className="indicateur-description">
                        Unité par défaut: {uniteDefault || "Non définie"}
                      </span>
                    </div>
                  </label>
                  {isIndicateurSelected(indicateur.id) && (
                    <label className="obligatoire-checkbox">
                      <input
                        type="checkbox"
                        checked={isIndicateurObligatoire(indicateur.id)}
                        onChange={() => onObligatoireToggle(poste.id, indicateur.id)}
                      />
                      <span className="obligatoire-label">Obligatoire *</span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {indicateurs && indicateurs.length === 0 && !indicateursLoading && !indicateursError && (
          <div className="no-indicateurs">
            <span>Aucun indicateur disponible pour ce poste</span>
          </div>
        )}
      </div>
    </div>
  );
};
